import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { interpolateText } from "@lib/utils/interpolateText";
import type { ReactNode } from "react";
import type { MicrocopyContextState, MicrocopyGetOptions, MicrocopySetKey } from "./types";
import { LogMicrocopyError, LogMicrocopyType } from "./types";

const loggedMissingKeys = new Set<string>();
export const MAX_LOGGED_KEYS = 1000;
export const loggedKeyQueue: string[] = [];

/**
 * Pure helper that resolves a single microcopy value from a given context state.
 * Used by both `useMicrocopy()` (client) and `getMicrocopyServer()` (server).
 */
export function getMicrocopyValue<T extends boolean = false>(
    context: MicrocopyContextState,
    microcopySetKey: MicrocopySetKey,
    microcopyKey: string,
    options?: MicrocopyGetOptions & { jsx?: T }
): T extends true ? ReactNode | null : string | null {
    const microcopySet = context[microcopySetKey];
    const mcType = options?.fromMultiple
        ? LogMicrocopyType.GET_MULTIPLE
        : LogMicrocopyType.GET_SINGLE;

    if (!microcopySet) {
        logMissingMicrocopy(
            microcopyKey,
            microcopySetKey,
            mcType,
            LogMicrocopyError.MISSING_MICROCOPY_SET
        );
        return null as any;
    }

    const result = microcopySet[microcopyKey];

    if (!result) {
        // eslint-disable-next-line turbo/no-undeclared-env-vars
        if (!process.env.JEST_WORKER_ID && !options?.fromMultiple) {
            logMissingMicrocopy(
                microcopyKey,
                microcopySetKey,
                mcType,
                LogMicrocopyError.MISSING_MICROCOPY
            );
        }
        return null as any;
    }

    if (!options?.replacements || Object.keys(options.replacements).length === 0) {
        return result.value as any;
    }

    const jsxEnabled = options?.jsx ?? false;
    const interpolated = interpolateText(result.value, options.replacements);

    if (!jsxEnabled && Array.isArray(interpolated)) {
        return interpolated.map((p) => (typeof p === "string" ? p : "")).join("") as any;
    }

    return interpolated as any;
}

/**
 * Pure helper that resolves multiple microcopy values from a given context state.
 * Used by both `useMicrocopy()` (client) and `getMicrocopyServer()` (server).
 */
export function getMicrocopyValues(
    context: MicrocopyContextState,
    microcopySetKey: MicrocopySetKey,
    microcopyKeys: readonly string[],
    options?: MicrocopyGetOptions
): Record<string, string> {
    const result: Record<string, string> = {};
    const getOptions = { ...(options ?? {}), fromMultiple: true, jsx: false as const };
    microcopyKeys.forEach((key) => {
        const microcopy = getMicrocopyValue<false>(context, microcopySetKey, key, getOptions);
        if (microcopy) {
            result[key] = microcopy;
        }
    });
    return result;
}

export function logMissingMicrocopy(
    key: string,
    set: string,
    mcType: LogMicrocopyType,
    type: LogMicrocopyError
): void {
    const uniqueId = `${set}:${key}`;

    if (loggedMissingKeys.has(uniqueId) || process.env.NEXT_PUBLIC_ENVIRONMENT !== "production") {
        return;
    }

    if (loggedMissingKeys.size >= MAX_LOGGED_KEYS) {
        const oldestKey = loggedKeyQueue.shift();
        if (oldestKey) loggedMissingKeys.delete(oldestKey);
    }

    loggedMissingKeys.add(uniqueId);
    loggedKeyQueue.push(uniqueId);

    Logger.warn(ServiceType.CONTENTFUL, `Missing microcopy "${key}" in "${set}"`, {
        tag: LogTag.MICROCOPY,
        key,
        set,
        pathName: typeof window !== "undefined" ? window.location.pathname : "",
        type: mcType,
        error: {
            name: type,
        },
    });
}
